import React from 'react';
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl';
import { CheckCircle2, Copy, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

import { toHumanTxId } from '../../../util/shortUuid';
import { usePickupCode, useReissuePickup } from '../../../ducks/pickup.duck';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';

const PICKUP_CODE_GROUP_AT = 3;

const T = {
  roleLabel: 'EscrowCard.roleLabel',
  fundsHeldTitle: 'EscrowCard.fundsHeldTitle',
  releasedTitle: 'EscrowCard.releasedTitle',
  statusBuyerHoldsCode: 'EscrowCard.statusBuyerHoldsCode',
  statusCompleted: 'EscrowCard.statusCompleted',
  pendingDescription: 'EscrowCard.pendingDescription',
  releasedDescription: 'EscrowCard.releasedDescription',
  orderId: 'EscrowCard.orderId',
  codeLabel: 'EscrowCard.codeLabel',
  expiresAt: 'EscrowCard.expiresAt',
  expiredTitle: 'EscrowCard.expiredTitle',
  expiredDescription: 'EscrowCard.expiredDescription',
  copyCode: 'EscrowCard.copyCode',
  copied: 'EscrowCard.copied',
  reissue: 'EscrowCard.reissue',
  reissueInProgress: 'EscrowCard.reissueInProgress',
  reissueError: 'EscrowCard.reissueError',
  loadError: 'EscrowCard.loadError',
  loading: 'EscrowCard.loading',
  unavailableMessage: 'EscrowCard.unavailableMessage',
};

/**
 * Format a 6-digit pickup code as `XXX XXX` for readability.
 *
 * @param {string|null|undefined} code
 * @returns {string}
 */
const formatGroupedCode = code => {
  if (!code) return '';
  return code.length > PICKUP_CODE_GROUP_AT
    ? `${code.slice(0, PICKUP_CODE_GROUP_AT)} ${code.slice(PICKUP_CODE_GROUP_AT)}`
    : code;
};

/** Render a timestamp as `Mon DD, YYYY · HH:mm TZ` in the active locale +
 * the viewer's timezone abbreviation. Showing the zone matters for pickup
 * coordination - two parties can be on different sides of a DST flip or in
 * different timezones, and "expires at 5pm" is ambiguous without it. */
const formatTimestamp = ts => (
  <>
    <FormattedDate value={ts} year="numeric" month="short" day="2-digit" />
    {' · '}
    <FormattedTime value={ts} timeZoneName="short" />
  </>
);

const ArticleShell = ({ inline, children }) => (
  <article
    className={
      inline
        ? 'bg-[linear-gradient(180deg,rgba(245,255,249,1),rgba(239,250,244,1))] px-4 py-2'
        : 'rounded-[20px] border border-green-500/20 bg-[linear-gradient(180deg,rgba(245,255,249,1),rgba(239,250,244,1))] p-3 shadow-[0_18px_36px_-32px_rgba(34,197,94,0.25)]'
    }
  >
    {children}
  </article>
);

const HeaderRow = ({ titleId, statusId, statusVariant }) => (
  <div className="flex items-start justify-between gap-3">
    <div>
      <h4 className="mt-1 font-heading text-base font-bold uppercase text-green-800">
        <FormattedMessage id={titleId} />
      </h4>
    </div>
  </div>
);

const OrderIdTile = ({ humanId }) => (
  <div className="rounded-xl border border-green-500/20 bg-white/90 p-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-green-700/70">
      <FormattedMessage id={T.orderId} />
    </p>
    <p className="mt-1 font-mono text-sm font-semibold text-foreground">#{humanId}</p>
  </div>
);

const CodePanel = ({ code, expiresAt }) => (
  <div className="mt-3 rounded-xl border-2 border-dashed border-green-500/30 bg-white/90 p-3 text-center">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-green-700/70">
      <FormattedMessage id={T.codeLabel} />
    </p>
    <p
      data-sentry-mask="true"
      className="mt-2 font-mono text-3xl font-bold tracking-[0.28em] text-primary"
    >
      {formatGroupedCode(code)}
    </p>
    {expiresAt ? (
      <p className="mt-1 text-[11px] text-green-700/70">
        <FormattedMessage id={T.expiresAt} values={{ when: formatTimestamp(expiresAt) }} />
      </p>
    ) : null}
  </div>
);

const VerifiedView = ({ humanId, verifiedAt, inline }) => (
  <ArticleShell inline={inline}>
    <HeaderRow
      titleId={T.releasedTitle}
      statusId={T.statusCompleted}
      statusVariant="statusSuccess"
    />
    <p className="mt-2 text-sm leading-6 text-green-900/80">
      <FormattedMessage
        id={T.releasedDescription}
        values={verifiedAt ? { when: formatTimestamp(verifiedAt) } : { when: '' }}
      />
    </p>
    {humanId ? (
      <div className="mt-3">
        <OrderIdTile humanId={humanId} />
      </div>
    ) : null}
  </ArticleShell>
);

const PendingView = ({
  humanId,
  code,
  expiresAt,
  onCopy,
  copied,
  onReissue,
  reissueInProgress,
  inline,
}) => (
  <ArticleShell inline={inline}>
    <HeaderRow
      titleId={T.fundsHeldTitle}
      statusId={T.statusBuyerHoldsCode}
      statusVariant="statusInfo"
    />
    <p className="mt-2 text-sm leading-6 text-green-900/80">
      <FormattedMessage id={T.pendingDescription} />
    </p>

    <CodePanel code={code} expiresAt={expiresAt} />
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Button onClick={onCopy} variant="outline" className="w-full font-heading uppercase">
        {copied ? (
          <>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            <FormattedMessage id={T.copied} />
          </>
        ) : (
          <>
            <Copy className="mr-2 h-4 w-4" />
            <FormattedMessage id={T.copyCode} />
          </>
        )}
      </Button>
      <Button
        onClick={onReissue}
        variant="ghost"
        disabled={reissueInProgress}
        className="w-full font-heading uppercase"
      >
        {reissueInProgress ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <FormattedMessage id={T.reissueInProgress} />
          </>
        ) : (
          <>
            <RefreshCw className="mr-2 h-4 w-4" />
            <FormattedMessage id={T.reissue} />
          </>
        )}
      </Button>
    </div>
  </ArticleShell>
);

const ExpiredView = ({ humanId, onReissue, reissueInProgress, inline }) => (
  <ArticleShell inline={inline}>
    <HeaderRow
      titleId={T.expiredTitle}
      statusId={T.statusBuyerHoldsCode}
      statusVariant="statusWarning"
    />
    <p className="mt-2 text-sm leading-6 text-green-900/80">
      <FormattedMessage id={T.expiredDescription} />
    </p>
    {humanId ? (
      <div className="mt-3">
        <OrderIdTile humanId={humanId} />
      </div>
    ) : null}
    <Button
      onClick={onReissue}
      disabled={reissueInProgress}
      className="mt-3 h-11 w-full font-heading uppercase"
    >
      {reissueInProgress ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <FormattedMessage id={T.reissueInProgress} />
        </>
      ) : (
        <>
          <RefreshCw className="mr-2 h-4 w-4" />
          <FormattedMessage id={T.reissue} />
        </>
      )}
    </Button>
  </ArticleShell>
);

const StatusMessage = ({ icon: Icon, messageId, inline }) => (
  <ArticleShell inline={inline}>
    <div className="flex items-center gap-2 text-sm text-green-800/80">
      <Icon className="h-4 w-4 flex-shrink-0" />
      <FormattedMessage id={messageId} />
    </div>
  </ArticleShell>
);

/** Truthy expiry that has already passed. */
const isExpired = expiresAt => (expiresAt ? new Date(expiresAt).getTime() < Date.now() : false);

/** Buyer-facing escrow / pickup-code card. */
const EscrowCard = ({ transactionId, inline = false }) => {
  const txId = transactionId?.uuid || '';
  const humanId = toHumanTxId(txId);
  const [copied, setCopied] = React.useState(false);

  const { data, error, isLoading, refetch } = usePickupCode(txId);
  const { reissue, isLoading: reissueInProgress, error: reissueError } = useReissuePickup(txId);

  React.useEffect(() => {
    if (txId) refetch();
  }, [txId, refetch]);

  if (!txId) return null;

  const code = data?.code || null;
  const verifiedAt = data?.verifiedAt;
  const expiresAt = data?.expiresAt;
  const expired = !verifiedAt && isExpired(expiresAt);

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      /* ignore: fall back to plain text in the panel */
    }
  };

  const handleReissue = () => {
    if (!txId || reissueInProgress) return;
    reissue();
  };

  if (isLoading) return <StatusMessage icon={Loader2} messageId={T.loading} inline={inline} />;
  if (error) return <StatusMessage icon={AlertCircle} messageId={T.loadError} inline={inline} />;
  if (verifiedAt) return <VerifiedView humanId={humanId} verifiedAt={verifiedAt} inline={inline} />;
  if (expired)
    return (
      <ExpiredView
        humanId={humanId}
        onReissue={handleReissue}
        reissueInProgress={reissueInProgress}
        inline={inline}
      />
    );
  if (code)
    return (
      <>
        <PendingView
          humanId={humanId}
          code={code}
          expiresAt={expiresAt}
          onCopy={handleCopy}
          copied={copied}
          onReissue={handleReissue}
          reissueInProgress={reissueInProgress}
          inline={inline}
        />
        {reissueError ? (
          <p className="mt-2 text-xs text-destructive text-center">
            <FormattedMessage id={T.reissueError} />
          </p>
        ) : null}
      </>
    );

  return <StatusMessage icon={AlertCircle} messageId={T.unavailableMessage} inline={inline} />;
};

export default EscrowCard;
